import React, { useCallback, useEffect, useMemo } from "react";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import { soundManager } from "../../../../SoundManager";
import { incrementBet } from "../../../../State/actions";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import {
  selectIsSpinning,
  selectUserInterfaceDisabled,
  selectSessionExpired,
  selectBet,
  selectBets,
  selectBalance,
  selectAutoPlayActive,
  selectAnyFeatureActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";

const BonusButtonIncrementBet = ({ x = 0, y = 0, size }) => {
  const dispatch = useDispatch();

  const isSpinning = useSelector(selectIsSpinning);
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const balance = useSelector(selectBalance);
  const sessionExpired = useSelector(selectSessionExpired);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const anyFeatureActive = useSelector(selectAnyFeatureActive);
  const replaySessionActive = useSelector(selectReplaySessionActive);

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters } =
    useButtonBrightness();


  const texture = getNewUITexture("icon_plus");

  // Memoize calculations
  const currentBetIndex = useMemo(() => bets.indexOf(bet), [bets, bet]);
  const nextBetAmount = useMemo(
    () => bets[currentBetIndex + 1],
    [bets, currentBetIndex]
  );

  const canIncreaseBet = useMemo(
    () =>
      currentBetIndex < bets.length - 1 &&
      balance >= nextBetAmount &&
      !isSpinning &&
      !sessionExpired &&
      !autoPlayActive &&
      !userInterfaceDisabled &&
      !anyFeatureActive &&
      !replaySessionActive,
    [
      currentBetIndex,
      bets.length,
      balance,
      nextBetAmount,
      isSpinning,
      sessionExpired,
      autoPlayActive,
      userInterfaceDisabled,
      anyFeatureActive,
      replaySessionActive,
    ]
  );

  // Memoize hitArea calculation (radius in world px — Graphics isn't scaled
  // by width/height like the Sprite is, so this matches the visual size)
  const hitRadius = useMemo(() => (size / 2) * 1.4, [size]);
  const hitArea = useMemo(() => new PIXI.Circle(0, 0, hitRadius), [hitRadius]);

  const handleClick = useCallback(() => {
    if (canIncreaseBet) {
      soundManager.play(SOUND_NAME.button_click);

      if (buttonRef.current) {
        triggerPress();
      }

      dispatch(incrementBet());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canIncreaseBet, dispatch]);

  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  const drawHitArea = useCallback(
    (g) => {
      g.clear();
      // Nearly invisible fill (required for hit detection to work)
      g.beginFill(0x000000, 0.01);
      g.drawCircle(0, 0, hitRadius);
      g.endFill();
    },
    [hitRadius]
  );

  return (
    <Container x={x} y={y}>
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="bonus_button_increment_bet_interactive_area"
        eventMode={canIncreaseBet ? "static" : "none"}
        cursor={canIncreaseBet ? "pointer" : "default"}
        hitArea={hitArea}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={drawHitArea}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="bonus_button_increment_bet"
        anchor={0.5}
        width={size}
        height={size}
        x={0}
        y={0}
        eventMode="none"
        alpha={canIncreaseBet ? 1 : 0.5}
      />
    </Container>
  );
};

export default BonusButtonIncrementBet;
